import { Box, IconButton, styled, Typography } from "@mui/material";
import ActionButton from "controls/global/action-button";
import { useSignatureDefaultDialogProvider } from "pages/signatures/providers/signature-defaults/SignatureDefaultDialogProvider";
import React, { useRef } from "react";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  gradients,
  iconSize,
  padding,
} from "theme/defaultStyle";
import { useSignatureDefaults } from "utils/context/SignatureDefaultsContext";
import {
  StewartDialog,
  StewartDialogActions,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import SignatureDefaultForm from "../default-form/SignatureDefaultForm";
import AddIcon from "theme/icons/AddIcon";
import CloseIcon from "@mui/icons-material/Close";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "800px",
    minWidth: "800px",
    display: "flex",
    padding: `${padding.xlarge21_5} ${padding.xlarge22} ${padding.xlarge22} ${padding.xlarge22}`,
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: `${gaps.large1}`,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled(Box)({
  background: gradients.blueGradient08,
  borderRadius: borderRadius.large,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.grey24,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  padding: `${padding.large1} ${padding.zero} ${padding.zero}`,
  flexShrink: 0,
});

type Props = {
  refreshPage?: () => void;
};

const SignatureDefaultDialog = ({ refreshPage }: Props) => {
  const { mode, isOpen, saveEnabled, cancelDialog } =
    useSignatureDefaultDialogProvider();
  const [, { resetState }] = useSignatureDefaults();
  const saveRef = useRef<HTMLButtonElement>(null);

  const handleCancel = () => {
    cancelDialog();
    resetState();
  };

  const handleSave = () => {
    saveRef && saveRef.current?.click();
  };

  return (
    <StyledDialog open={isOpen} height={700}>
      <StyledDialogTitle>
        <IconContainer>
          <AddIcon />
        </IconContainer>
        <Typography>
          {mode === "EDIT" ? "View/Edit  Default Signature" : "Add Default Signature"}
        </Typography>
        <CloseIconContainer>
          <IconButton className="iconButton" onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>
        <SignatureDefaultForm saveRef={saveRef} refreshPage={refreshPage} />
      <StyledDialogAction>
        <ActionButton
          variant="outlined"
          color="secondary"
          onClick={() => handleCancel()}
          disabled={false}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          disabled={!saveEnabled}
          onClick={handleSave}
        >
          {mode === "EDIT" ? "Update Signature" : "Add Default"}
        </ActionButton>
      </StyledDialogAction>
    </StyledDialog>
  );
};

export default SignatureDefaultDialog;
